import React from 'react';
import styled from 'styled-components';
import { useTheme } from 'styled-components';
import { SHADOW_CARD } from './styles';
import { Body1, Body5, Body7, Container } from '@ui';
import { FlatButton, TransparentFlatButton } from '@ui/buttons';
import { SkeletonButton } from '@ui/buttons/SkeletonButton';

const PreviewCard = styled.div`
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  border-radius: 8px;
  box-shadow: ${SHADOW_CARD};
  overflow: hidden;
  pointer-events: none;
`;

const PreviewInner = styled.div`
  padding: 20px;
`;

const PreviewHeaderBar = styled.div`
  background: ${({ theme }) => theme.themeColors.header};
  border-bottom: 1px solid ${({ theme }) => theme.themeColors.border};
  padding: 10px 16px;
  border-radius: 4px 4px 0 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HeaderDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ theme }) => theme.themeColors.primaryCtaButton};
  opacity: 0.8;
`;

const SectionDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.themeColors.lowContrastBorder};
  margin: 16px 0;
`;

const SampleCard = styled.div`
  background: ${({ theme }) => theme.themeColors.appBackground};
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 4px;
  padding: 10px 12px;
`;

const SemanticBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: bold;
  background: ${({ $color }) => `${$color}22`};
  color: ${({ $color }) => $color};
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

const SmallButtonWrap = styled.div`
  button {
    padding: 7px 14px;
    font-size: 12px;
  }
`;

export const PreviewPanel = () => {
  const theme = useTheme();
  const themeColors = theme?.themeColors || {};

  return (
    <PreviewCard>
      <PreviewHeaderBar>
        <HeaderDot />
        <Body7 color={themeColors.text}>Tornado</Body7>
      </PreviewHeaderBar>

      <PreviewInner>
        <Container bottom={4} row spaceBetween verticallyCenter>
          <Body1 bold>Preview</Body1>
          <Body7 isLowContrast>Updates as you edit</Body7>
        </Container>

        <SectionDivider />

        <Container bottom={12}>
          <Body5 bold>Sample content</Body5>
          <Container top={4}>
            <Body5>
              The quick analysis shows strong momentum across key indicators. Revenue growth is tracking ahead of
              estimates.
            </Body5>
          </Container>
          <Container top={4}>
            <Body5 isLowContrast>Last updated today at 9:41 AM — data sourced from filings.</Body5>
          </Container>
        </Container>

        <SectionDivider />

        <Container bottom={12}>
          <Body7 isLowContrast>Actions</Body7>
          <Container top={8}>
            <ButtonRow>
              <SmallButtonWrap>
                <FlatButton onClick={() => {}}>Primary</FlatButton>
              </SmallButtonWrap>
              <SmallButtonWrap>
                <SkeletonButton onClick={() => {}}>Secondary</SkeletonButton>
              </SmallButtonWrap>
              <SmallButtonWrap>
                <TransparentFlatButton onClick={() => {}}>Tertiary</TransparentFlatButton>
              </SmallButtonWrap>
            </ButtonRow>
          </Container>
        </Container>

        <SectionDivider />

        <Container bottom={12}>
          <Body7 isLowContrast>Sentiment indicators</Body7>
          <Container top={8}>
            <BadgeRow>
              <SemanticBadge $color={themeColors.positive}>+12.4%</SemanticBadge>
              <SemanticBadge $color={themeColors.neutral}>0.0%</SemanticBadge>
              <SemanticBadge $color={themeColors.negative}>−3.2%</SemanticBadge>
              <SemanticBadge $color={themeColors.proColor}>Pro</SemanticBadge>
              <SemanticBadge $color={themeColors.conColor}>Con</SemanticBadge>
            </BadgeRow>
          </Container>
        </Container>

        <SectionDivider />

        <SampleCard>
          <Body7 isLowContrast>Panel surface</Body7>
          <Container top={4}>
            <Body5>Nested card using app background token</Body5>
          </Container>
        </SampleCard>
      </PreviewInner>
    </PreviewCard>
  );
};
